// /home/coder/project/frontend/src/pages/qaqc/inspections/QaqcInspFormDetail.tsx
import React from 'react';
import { Spin, Card, Row, Col, Typography, Result, message, Table } from 'antd';
import { ArrowLeftOutlined, AuditOutlined } from '@ant-design/icons'; // Changed icon
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchSqlData } from '../../../services/api';
import type { VwQaqcInspFormDetails } from '../../../types'; // QA/QC specific type
import Button from '../../../components/common/Button';

const { Title } = Typography;

const QaqcInspFormDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const designId = id ? parseInt(id, 10) : undefined;

  // --- Fetch Form Design Details ---
  const {
    data: formDetails, // This will be an array of VwQaqcInspFormDetails
    isLoading,
    isError,
    error,
  } = useQuery<VwQaqcInspFormDetails[], Error>({
    queryKey: ['qaqcFormDetail', designId], // Query key specific to QA/QC
    queryFn: async () => {
      if (!designId) throw new Error("No valid form design ID provided");
      // Fetch all rows from the view matching the design_id
      const results = await fetchSqlData('vw_qaqc_insp_formdetails', 'design_id', designId); // QA/QC view
      if (!results || !Array.isArray(results)) {
        throw new Error(`Invalid response when fetching QA/QC form details for ID ${designId}.`);
      }
      return results;
    },
    enabled: !!designId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // --- Loading and Error Handling ---
  if (!designId) {
    return (
      <Result status="error" title="Invalid QA/QC Form Design ID" subTitle="No valid numeric ID provided in the URL."
        extra={<Button type="primary" onClick={() => navigate('/qaqc/inspections/form_list')}>Go Back</Button>} />
    );
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    const errorMsg = (error as Error)?.message || 'Failed to load QA/QC form details.';
    message.error(errorMsg);
    return (
      <Result status="error" title="Error Loading QA/QC Form Design" subTitle={errorMsg}
        extra={<Button type="primary" onClick={() => navigate('/qaqc/inspections/form_list')}>Go Back</Button>} />
    );
  }

  if (!formDetails || formDetails.length === 0) {
    return (
      <Result status="warning" title="QA/QC Form Design Not Found" subTitle={`Could not find details for QA/QC form design ID: ${designId}`}
        extra={<Button type="primary" onClick={() => navigate('/qaqc/inspections/form_list')}>Go Back</Button>} />
    );
  }

  const formInfo = formDetails[0]; // Common info

  const fieldColumns = [
    { title: 'Discipline', dataIndex: 'discipline', key: 'discipline', render: (text: string | null) => text || '-' },
    { title: 'Category', dataIndex: 'category', key: 'category', render: (text: string | null) => text || '-' },
    { title: 'Check Item', dataIndex: 'check_item', key: 'check_item', render: (text: string | null) => text || '-' },
    { title: 'Field ID', dataIndex: 'field_id', key: 'field_id' }
  ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 32 }}>
      <Row align="middle" gutter={16} style={{ marginBottom: 32 }}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qaqc/inspections/form_list')} size="large">Back</Button>
        </Col>
        <Col flex="auto">
          <Title level={4} style={{ margin: 0 }}>
            <AuditOutlined style={{ marginRight: 8 }} /> {/* Changed icon */}
            QA/QC Form Design: {formInfo.form_name} ({formInfo.form_num})
          </Title>
        </Col>
      </Row>
      <Card title="Form Fields" bordered={false}>
        <Table
          columns={fieldColumns}
          dataSource={formDetails}
          rowKey="field_id"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default QaqcInspFormDetail;